/* 
* Environment Configuration
* Provides access to environment-specific settings
* 
* Environment variables are injected at build time by Parcel from:
* 1. .env file in project root
* 2. app.config.yaml env section
* 3. System environment variables (including CI/CD pipeline variables)
*/

/**
 * Valid environment values for API tier selection
 */
export type EnvironmentTier = 'local' | 'dev' | 'stage' | 'prod'

/**
 * Hostnames where dev token functionality is allowed (when ?devtokenmode=true is present)
 * Includes localhost and all dev/developer namespace workspaces
 */
export const DEV_TOKEN_ALLOWED_HOSTNAMES = [
  'localhost',
  '127.0.0.1',
  // Main dev instance
  '14257-emc-dev.adobeio-static.net',
  // Developer namespace workspaces
  '14257-emc-qiyundai.adobeio-static.net',
  '14257-emc-sharmeeb.adobeio-static.net',
  '14257-emc-rkhan.adobeio-static.net',
]

/**
 * Whether the dev token system is enabled.
 * True only when (1) hostname is in the allowlist AND (2) URL has ?devtokenmode=true.
 * Use this to show the Dev Token UI and to use stored dev tokens for API auth.
 */
export function isDevTokenModeEnabled(): boolean {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname
  const devTokenMode = new URLSearchParams(window.location.search).get('devtokenmode') === 'true'
  return DEV_TOKEN_ALLOWED_HOSTNAMES.includes(hostname) && devTokenMode
}

/**
 * Environment configuration
 * Parcel automatically injects process.env variables at build time
 */
export const env = {
  // Per-environment client identities for x-client-identity API header
  // Loaded from .env file: DEV_CLIENT_IDENTITY, STAGE_CLIENT_IDENTITY, PROD_CLIENT_IDENTITY
  DEV_CLIENT_IDENTITY: process.env.DEV_CLIENT_IDENTITY || 'emc-console-dev',
  STAGE_CLIENT_IDENTITY: process.env.STAGE_CLIENT_IDENTITY || '',
  PROD_CLIENT_IDENTITY: process.env.PROD_CLIENT_IDENTITY || '',
  
  // API Key for external requests
  API_KEY: process.env.API_KEY || 'acom_event_service',

  // ============================================================================
  // IMS AUTHENTICATION (Standalone mode - without Experience Cloud Shell)
  // ============================================================================
  // IMS Client ID registered with IDOPS/IMSS — used only in standalone (imslib) mode.
  // In ExC Shell mode the shell's own exc_app client ID is used automatically.
  // Loaded from .env file: IMS_CLIENT_ID=your-client-id
  IMS_CLIENT_ID: process.env.IMS_CLIENT_ID || 'acom_event_mgmt_console',

  // OAuth scopes for IMS authentication (standalone mode)
  // Loaded from .env file: IMS_SCOPES=AdobeID,gnav,openid
  IMS_SCOPES: process.env.IMS_SCOPES || 'AdobeID,gnav,openid',

  // IMS environment is derived automatically from ENVIRONMENT tier.
  // 'prod' tier -> 'prod' IMS; everything else (dev/stage/personal) -> 'stg1' IMS.
  // See getImsEnvironment() in config/constants.ts.

  /**
   * Environment tier for API endpoint selection
   * Set at build time via CI/CD or .env file
   * Values: 'dev', 'stage', 'prod'
   * Defaults to 'dev' for local development
   */
  ENVIRONMENT: (process.env.ENVIRONMENT || 'dev') as EnvironmentTier,
  
  // Google Places API Keys (environment-specific)
  // Loaded from .env file: DEV_GOOGLE_PLACES_API, STAGE_GOOGLE_PLACES_API, PROD_GOOGLE_PLACES_API
  DEV_GOOGLE_PLACES_API: process.env.DEV_GOOGLE_PLACES_API || '',
  STAGE_GOOGLE_PLACES_API: process.env.STAGE_GOOGLE_PLACES_API || '',
  PROD_GOOGLE_PLACES_API: process.env.PROD_GOOGLE_PLACES_API || '',
  
  // Environment mode
  NODE_ENV: process.env.NODE_ENV || 'development',
  
  // Check if on a development host (localhost or dev instances).
  // For dev token UI and API usage, use isDevTokenModeEnabled() which also requires ?devtokenmode=true.
  isDevelopment: () => {
    const hostname = window.location.hostname
    return DEV_TOKEN_ALLOWED_HOSTNAMES.includes(hostname)
  },

  /** Dev token system only active when hostname is allowed AND URL has ?devtokenmode=true */
  isDevTokenModeEnabled,
  
  // Check if running on localhost specifically
  isLocalhost: () => {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1'
  },
  
  // Check if in production mode
  isProduction: () => {
    return !env.isDevelopment()
  }
}

/**
 * Get the client identity for the current environment tier.
 * Mirrors the same per-env selection pattern used for Google Places API keys.
 */
export function getClientIdentity(): string {
  if (env.ENVIRONMENT === 'prod') return env.PROD_CLIENT_IDENTITY
  if (env.ENVIRONMENT === 'stage') return env.STAGE_CLIENT_IDENTITY
  return env.DEV_CLIENT_IDENTITY
}

export default env
